import { useState } from 'react';
import {
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  DropdownItem,
  DropdownCheckboxItem,
  DropdownSeparator,
} from '@shelflix/ui-web/components/ui/dropdown';
import { cn } from '@/utils/utils';
import {
  LucidePlusSquare,
  ChevronDownIcon,
  LucideTrash,
  LucideEdit,
  LucideXSquare,
  LucideCheckSquare2,
} from 'lucide-react';
import { queryClient, trpc } from '@/trpcClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertModal } from '../AlertModal';
import { Button } from '@shelflix/ui-web/components/ui/button';

interface TagsDropdownProps {
  selectedTags: { uuid: string; name: string }[];
  setSelectedTags: (tags: { uuid: string; name: string }[]) => void;
}

export function TagsDropdown({ selectedTags, setSelectedTags }: TagsDropdownProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editedTagName, setEditedTagName] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<{ uuid: string; name: string } | null>(null);

  const handleDisplaySelectedTags = () => {
    if (selectedTags.length === 0) {
      return 'Selecione uma tag';
    } else {
      return selectedTags.map((tag) => tag.name).join(', ');
    }
  };

  const { data: tags = [], isLoading } = useQuery({
    ...trpc.tag.list.queryOptions({}),
  });

  const createTagMutation = useMutation(
    trpc.tag.create.mutationOptions({
      onSuccess: (newTag) => {
        queryClient.invalidateQueries(trpc.tag.list.pathFilter({}));
        tags.push(newTag[0]);
        setSelectedTags([...selectedTags, { uuid: newTag[0].uuid, name: newTag[0].name }]);
      },
      onError: (error) => {
        toast.error(error.message || 'Erro ao criar tag');
      },
    })
  );

  const deleteTagMutation = useMutation(
    trpc.tag.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries(trpc.tag.list.pathFilter({}));
      },
      onError: (error) => {
        toast.error(error.message || 'Erro ao excluir tag');
      },
    })
  );

  const updateTagMutation = useMutation(
    trpc.tag.edit.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries(trpc.tag.list.pathFilter({}));
      },
      onError: (error) => {
        toast.error(error.message || 'Erro ao atualizar tag');
      },
    })
  );

  const inputRef = (input: HTMLInputElement | null) => {
    if (input) {
      input.focus();
    }
  };

  return (
    <>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={(open) => {
          setDropdownOpen(open);
          if (!open) {
            setAddingTag(false);
            setNewTagName('');
            setEditingTag(null);
            setEditedTagName('');
          }
        }}
      >
        <DropdownTrigger disabled={isLoading} asChild>
          <button
            className={cn(
              'border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex h-10 w-full cursor-pointer items-center justify-between rounded-md border px-3 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
              selectedTags.length > 0 ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {handleDisplaySelectedTags()}
            <ChevronDownIcon
              className={cn('h-4 w-4 transition-transform ease-in-out', dropdownOpen ? 'rotate-0' : '-rotate-90')}
            />
          </button>
        </DropdownTrigger>

        <DropdownContent>
          {tags.length ? (
            tags.map((tag) => (
              <DropdownCheckboxItem
                key={tag.id}
                checked={selectedTags.some((selectedTag) => selectedTag.uuid === tag.uuid)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedTags([...selectedTags, tag]);
                  } else {
                    setSelectedTags(selectedTags.filter((selectedTag) => selectedTag.uuid !== tag.uuid));
                  }
                }}
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                {editingTag === tag.uuid ? (
                  <div
                    className="flex w-full items-center justify-between gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      value={editedTagName}
                      onChange={(e) => setEditedTagName(e.target.value)}
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex-1 rounded-md border px-2 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      onBlur={(e) => {
                        if (editingTag === tag.uuid) {
                          e.target.focus();
                        }
                      }}
                    />
                    <div className="flex items-center">
                      <button
                        className="cursor-pointer p-1 hover:text-red-500"
                        onClick={() => {
                          setEditingTag(null);
                          setEditedTagName('');
                        }}
                      >
                        <LucideXSquare className="size-5" />
                      </button>
                      <button
                        className="cursor-pointer p-1 hover:text-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => {
                          if (editedTagName.trim()) {
                            updateTagMutation.mutate({ uuid: tag.uuid, name: editedTagName.trim() });
                            setEditingTag(null);
                            setEditedTagName('');
                          }
                        }}
                        disabled={editedTagName.trim() === ''}
                      >
                        <LucideCheckSquare2 className="size-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex w-full items-center justify-between gap-2">
                    {tag.name}
                    <div
                      className="flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <button
                        className="hover:text-primary cursor-pointer p-1"
                        onClick={() => {
                          setEditingTag(tag.uuid);
                          setEditedTagName(tag.name);
                          setAddingTag(false);
                          setNewTagName('');
                        }}
                      >
                        <LucideEdit className="size-5" />
                      </button>
                      <button
                        className="hover:text-primary cursor-pointer p-1"
                        onClick={() => {
                          setDropdownOpen(false);
                          setShowDeleteModal(true);
                          setTagToDelete({ uuid: tag.uuid, name: tag.name });
                        }}
                      >
                        <LucideTrash className="size-5" />
                      </button>
                    </div>
                  </div>
                )}
              </DropdownCheckboxItem>
            ))
          ) : (
            <DropdownItem>Nenhuma tag encontrada</DropdownItem>
          )}
          <DropdownSeparator />
          <DropdownItem>
            <div
              className="flex w-full items-center justify-between gap-2"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {!addingTag ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddingTag(true);
                    setEditingTag(null);
                    setEditedTagName('');
                  }}
                  className="hover:text-primary w-full cursor-pointer text-left"
                >
                  Adcionar nova tag
                </button>
              ) : (
                <>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Digite a nova tag"
                    className="border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex-1 rounded-md border px-2 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    onBlur={(e) => {
                      if (addingTag) {
                        e.target.focus();
                      }
                    }}
                    onChange={(e) => setNewTagName(e.target.value)}
                    value={newTagName}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (newTagName.trim() !== '') {
                        createTagMutation.mutate({ name: newTagName });
                        setNewTagName('');
                      }
                    }}
                    className="hover:text-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={newTagName.trim() === ''}
                  >
                    <LucidePlusSquare className="size-6" />
                  </button>
                </>
              )}
            </div>
          </DropdownItem>
        </DropdownContent>
      </Dropdown>
      <AlertModal
        icon={<LucideTrash className="size-6" />}
        title="Excluir tag"
        subtitle="Esta ação não pode ser desfeita."
        openModal={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        content={
          <p className="mb-4 text-center text-lg">
            Ao excluir esta tag, ela será removida de todos os materiais que a utilizam.
            <br />
            <strong>Tem certeza que deseja continuar?</strong>
          </p>
        }
        actionBotton={
          <div className="flex flex-row justify-between gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} size="lg">
              <span>Cancelar</span>
            </Button>
            <Button
              variant="default"
              onClick={() => {
                if (tagToDelete) {
                  deleteTagMutation.mutate({ uuid: tagToDelete.uuid });
                  setSelectedTags(selectedTags.filter((tag) => tag.uuid !== tagToDelete.uuid));
                  tags.splice(
                    tags.findIndex((t) => t.uuid === tagToDelete.uuid),
                    1
                  );
                  setShowDeleteModal(false);
                }
              }}
              size="lg"
            >
              <span>Excluir</span>
            </Button>
          </div>
        }
      />
    </>
  );
}
